import { makeStyles, Spinner } from "@fluentui/react-components";
import {
  AirplaneLandingFilled,
  AirplaneTakeOffFilled,
} from "@fluentui/react-icons";
import React, { useEffect, useState } from "react";
import { AmdDonutChart } from "../../../../components/common/charts/DonutChart/DonutChart";
import { LayoutContext } from "../../../../context/LayoutContext";
import {
  $cloud100,
  $paxArrival,
  $paxDeparture,
  $sky60,
  $white,
} from "../../../../cssjs/colors";
import { useApiCall } from "../../../../hooks/useApiCall";
import { IPassengerSummaryApiData } from "../../../../models/pax/IPaxTypes";
import { CommonHelper } from "../../../../utils/common/CommonHelper";
import { getApiUrl } from "../../../../utils/common/CommonHelperFunctions";
import { EMPTY_STATE_MESSAGE } from "../../../../utils/common/Constants";
import { PassengerHelper } from "../../../../utils/pax/PassengerHelper";
import { PersonWalkingLuggageIcon } from "../../../common/icons/person-walking-luggage/personWalkingLuggageIcon";
import { NoDataCard } from "../../../common/no-data-card/NoDataCard";
import OverviewCardHeader from "../cards/OverviewCardHeader";

interface IPaxCardProps {
  airport: string;
}

export const PaxCard: React.FunctionComponent<IPaxCardProps> = ({
  airport,
}): React.JSX.Element => {
  const [passengerData, setPassengerData] = useState<{
    arrival?: number;
    overall?: number;
    departure?: number;
  }>({});

  const [passengerSummaryData, setPassengerSummaryData] = useState<any>([]);
  const { globalFilterDate, timezone } = React.useContext(LayoutContext);

  const getPassengerSummaryUrl = getApiUrl(
    process.env.REACT_APP_API_PAX_SUMMARY,
    airport,
    globalFilterDate.startDate,
    globalFilterDate.endDate,
    timezone
  );

  const {
    data: passengerSummaryDataResponse,
    error,
    isLoading,
  } = useApiCall<IPassengerSummaryApiData>({
    url: getPassengerSummaryUrl,
    dependencies: [airport, globalFilterDate],
  });

  useEffect(() => {
    if (passengerSummaryDataResponse) {
      setPassengerSummaryData(
        Object.keys(passengerSummaryDataResponse).map((key: string) => {
          const obj = passengerSummaryDataResponse[key];
          return { type: key, ...obj };
        })
      );
    }
  }, [passengerSummaryDataResponse]);

  useEffect(() => {
    if (passengerSummaryData.length > 0) {
      const newPassengerData = { arrival: 0, overall: 0, departure: 0 };
      passengerSummaryData.forEach((item: any) => {
        if (item.type === "Overall") {
          newPassengerData.overall = item.kpiValue;
        } else if (item.type === "Arrivals") {
          newPassengerData.arrival = item.kpiValue;
        } else if (item.type === "Departures") {
          newPassengerData.departure = item.kpiValue;
        }
      });
      setPassengerData(newPassengerData);
    }
  }, [passengerSummaryData]);
  const classes = useStyles();

  return (
    <>
      {error && <NoDataCard message={EMPTY_STATE_MESSAGE.Passenger} />}
      {isLoading ? (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={classes.cardLoader}
        />
      ) : null}
      {!error && !isLoading ? (
        <section
          className={`${classes.cardContainer} d-flex amd-container-vcolumn`}
          data-testid="pax-card"
        >
          <OverviewCardHeader
            icon={<PersonWalkingLuggageIcon />}
            title="Passengers on ground"
            displayValue={CommonHelper.numberFomatter(
              passengerData?.overall ? passengerData.overall : 0
            )}
          />
          <div className="d-flex">
            <div
              className={`d-flex amd-container-vcolumn ${classes.label} pl-sm-2`}
            >
              <div className="d-flex">
                <div
                  className="d-flex"
                  style={{ top: "4px", position: "relative" }}
                >
                  <span
                    style={{ background: $paxArrival }}
                    className={`${classes.cardlegendSquare}`}
                  />
                </div>
                <div className={`fs-18 ${classes.cardLandingIcon} pr-sm-2`}>
                  <AirplaneLandingFilled />
                </div>
                <div className={`fs-14`}>
                  <div className={`${classes.cardLandingIcon} fw-500 pb-sm-1`}>
                    Arrivals
                  </div>
                  {passengerData?.arrival}
                </div>
              </div>
              <div className="d-flex">
                <div
                  className="d-flex"
                  style={{ top: "4px", position: "relative" }}
                >
                  <span
                    style={{ background: $paxDeparture }}
                    className={`${classes.cardlegendSquare}`}
                  />
                </div>
                <div className={`fs-18 ${classes.cardLandingIcon} pr-sm-2`}>
                  <AirplaneTakeOffFilled />
                </div>
                <div className={`fs-14 `}>
                  <div className={`${classes.cardLandingIcon} fw-500 pb-sm-1 `}>
                    Departures
                  </div>
                  {passengerData?.departure}
                </div>
              </div>
            </div>
            <AmdDonutChart
              resourceData={PassengerHelper.getArrivalDepartureDonutDataFromObject(
                passengerData
              )}
              noFlightsMessage="No Data found"
              hideLegend={true}
              radius={40}
              width={80}
              height={75}
              hideValueInsideDonut={false}
              donutContainerWidth="165px"
              chartAlignment="left"
              showCustomLegend={true}
              wrapperClassName="pl-3"
              fontSize="16px"
            />
          </div>
        </section>
      ) : null}
    </>
  );
};

const useStyles = makeStyles({
  cardLoader: {
    height: "100%",
  },
  cardLandingIcon: {
    color: $sky60,
  },
  title: { color: $sky60 },
  label: {
    justifyContent: "space-evenly",
  },
  cardContainer: {
    borderRadius: "8px",
    boxSizing: "border-box",
    background: $white,
    padding: "10px 8px",
    minHeight: "100%",
    color: $cloud100,
  },
  cardIcon: {
    marginTop: "-2px",
  },
  cardlegendSquare: {
    width: "10px",
    height: "10px",
    top: "20px",
    marginRight: "2px",
    display: "inline-block",
    lineHeight: "1.5",
  },
});
